import React, { Suspense, lazy } from "react";
import { Route, Routes, Link, Navigate } from "react-router-dom";
import PageSkeleton from "./components/shared/PageSkeleton";
import PageMeta from "./components/shared/PageMeta";
// Named once in routeChunks.js and used twice: by lazyRetry here, and by the
// preload table there, so the two cannot drift.
import {
  loadSocialHome,
  loadSocialFeed,
  loadCommunityListPage,
  loadChallengesPage,
  loadRecipeListPage,
  loadGameHub,
  loadKidsLearningHub,
} from './routeChunks';
// Agent is the landing page - eagerly loaded, no lazy/Suspense black screen
import Agent from "./components/Agent/Agent";
// Auth guards
import RoleGuard from './components/RoleGuard';
import { Box, Typography, Button } from '@mui/material';

// Prerendered SEO surfaces are imported eagerly, NOT via lazyRetry.
//
// scripts/prerender.js writes fully rendered HTML for all 263 of these
// routes. That HTML comes from Puppeteer, not from React SSR, so it carries
// none of the boundary markers (<!--$-->) that let React keep server markup
// while a lazy chunk loads. Hydration therefore found a suspended boundary
// with no marker, tore the prerendered content out, and painted
// <PageSkeleton> in its place -- so every SEO page showed real content, then
// a shimmer, then the same content again. Prerendering was paying for a
// worse first impression than not prerendering at all.
//
// These components are text pages; loading them with the entry bundle costs
// far less than the flash did. Routes NOT prerendered stay lazy, and the
// <Suspense> wrappers below are left in place -- harmless around a component
// that can no longer suspend, and one less thing to unpick if a route moves.
import BlogIndex from './pages/Blogs/BlogIndex';
import BlogReasoningPost from './pages/Blogs/reasonAiBlog';
import BlogPrivacyPost from './pages/Blogs/privacyBlog';
import BlogRunLocalAiPost from './pages/Blogs/runLocalAiBlog';
import BlogLearningEnginePost from './pages/Blogs/learningEngineBlog';
import BlogDynamicPage from './pages/Blogs/DynamicPage';
import HiveCensus from './pages/HiveCensus';
// The film inside it is NOT in this bundle: HowItWorks loads it after mount.
import HowItWorks from './pages/HowItWorks/HowItWorks';
import Download from './pages/Download';
import Press from './pages/Press';
import NewsIndex from './pages/News/NewsIndex';
import NewsArticlePage from './pages/News/NewsArticlePage';
import ListingsPage from './components/Listings/ListingsPage';
import ResearchIndex from './pages/Research/ResearchIndex';
import ResearchPaperPage from './pages/Research/ResearchPaperPage';
import AnswerIndex from './pages/Answers/AnswerIndex';
import AnswerPage from './pages/Answers/AnswerPage';
import EarnPage from './pages/Earn/EarnPage';
import JobsPage from './pages/Jobs/JobsPage';
import IosComingSoonPage from './pages/IosComingSoon/IosComingSoonPage';
import PrivacyPolicyPage from './pages/Legal/PrivacyPolicyPage';
import DataDeletionPage from './pages/Legal/DataDeletionPage';
import IncidentIndex from './pages/Incidents/IncidentIndex';
import IncidentPage from './pages/Incidents/IncidentPage';
// Per-agent capability page — a prerendered SEO surface, so eager like the
// others above (a lazy chunk here would re-introduce the #418 hydration
// mismatch these imports were made eager to avoid).
import AgentLanding from './pages/Agents/AgentLanding';

// Prerendered too, and eager for the same reason as the block above.
//
// These were still lazy(), so hydration hit a Suspense boundary whose chunk had
// not arrived, tore the prerendered content out and re-rendered it. React named
// it once the boundary was removed: "Expected server HTML to contain a matching
// <section> in <div>, at Pricings, at Suspense".
//
// It is a race, which is why it looked intermittent: /contact passed the
// hydration check while /pricing failed, on the same build, purely on whether
// the chunk won. Eager makes it deterministic.
//
// The /social family stays lazy on purpose. It is an application surface rather
// than a text page, and pulling it into the entry bundle would cost every
// visitor who never opens it. Those routes need their chunk preloaded before
// hydrateRoot instead; that is a separate piece of work.
import Pricing from "./pages/pricing";
import AboutUs from "./pages/aboutus";
import Contact from "./pages/contact";
import Institution from "./pages/institution";
import SpeechTherapyPage from "./pages/SpeechTherapyPage";
import TrialPlanPricing from "./pages/TrialPlanPricing";
import Personalised_Learning from "./pages/index-three";
import Agents from "./components/Agent/Agents";
import SignupLite from "./pages/signuplite";
// /about renders this and /about is prerendered, so it is eager for the same
// reason as the rest: a lazy chunk that has not arrived makes hydration discard
// the prerendered page and paint it again.
import Home from "./pages/Home";

/**
 * Retry wrapper for React.lazy - retries chunk loading up to 3 times
 * before giving up. Handles transient ChunkLoadError failures.
 */
function lazyRetry(importFn) {
  return lazy(() =>
    importFn().catch((err) => {
      // Only retry ChunkLoadError (network failures loading JS chunks)
      if (err.name === 'ChunkLoadError' || err.message?.includes('Loading chunk')) {
        return new Promise((resolve) => setTimeout(resolve, 1000))
          .then(() => importFn().catch(() =>
            new Promise((resolve) => setTimeout(resolve, 2000))
              .then(() => importFn())
          ));
      }
      throw err;
    })
  );
}


const PaymentFailure = lazy(() => import("../src/components/PaymentFailure"));
const PaymentSuccess = lazy(() => import("../src/components/PaymentSuccess"));
const PendingPaymentPage = lazy(() =>
  import("../src/components/PendingPaymentPage")
);
const NewSignup = lazy(() => import("./pages/NewSignup"));
const SigninPage = lazy(() => import("./pages/SigninPage"));
const SocialHome = lazyRetry(loadSocialHome);
const SocialFeed = lazyRetry(loadSocialFeed);
const SocialProfile = lazyRetry(() => import('./components/Social/Profile/ProfilePage'));
const SocialResonance = lazyRetry(() => import('./components/Social/Gamification/ResonanceDashboard'));
const SocialAchievements = lazyRetry(() => import('./components/Social/Gamification/AchievementsPage'));
const ChallengesPage = lazyRetry(loadChallengesPage);
const ChallengeDetailPage = lazyRetry(() => import('./components/Social/Gamification/ChallengeDetailPage'));
const SeasonPage = lazyRetry(() => import('./components/Social/Gamification/SeasonPage'));
const RegionsPage = lazyRetry(() => import('./components/Social/Regions/RegionsPage'));
const RegionDetailPage = lazyRetry(() => import('./components/Social/Regions/RegionDetailPage'));
const EncountersPage = lazyRetry(() => import('./components/Social/Encounters/EncountersPage'));
const EncounterDetailPage = lazyRetry(() => import('./components/Social/Encounters/EncounterDetailPage'));
const ActivityHub = lazyRetry(() => import('./components/Social/ActivityHub/ActivityHub'));
const ComputeDashboardPage = lazyRetry(() => import('./components/Social/Compute/ComputeDashboardPage'));
const ExperimentDiscoveryPage = lazyRetry(() => import('./components/Social/Experiments/ExperimentDiscoveryPage'));
const AgentEvolutionPage = lazyRetry(() => import('./components/Social/Evolution/AgentEvolutionPage'));
const CampaignsPage = lazyRetry(() => import('./components/Social/Campaigns/CampaignsPage'));
const CampaignStudio = lazyRetry(() => import('./components/Social/Campaigns/CampaignStudio'));
const CampaignDetailPage = lazyRetry(() => import('./components/Social/Campaigns/CampaignDetailPage'));
const PostDetailPage = lazyRetry(() => import('./components/Social/Post/PostDetailPage'));
const SearchPage = lazyRetry(() => import('./components/Social/Search/SearchPage'));
const NotificationsPage = lazyRetry(() => import('./components/Social/Notifications/NotificationsPage'));
const RecipeListPage = lazyRetry(loadRecipeListPage);
const RecipeDetailPage = lazyRetry(() => import('./components/Social/Recipes/RecipeDetailPage'));
const CommunityListPage = lazyRetry(loadCommunityListPage);
const CommunityDetailPage = lazyRetry(() => import('./components/Social/Communities/CommunityDetailPage'));

// Games Hub (Adult)
const GameHub = lazyRetry(loadGameHub);
const UnifiedGameScreen = lazyRetry(() => import('./components/Social/Games/UnifiedGameScreen'));

// Kids Learning Zone
const KidsLearningHub = lazyRetry(loadKidsLearningHub);
const KidsGameScreen = lazyRetry(() => import('./components/Social/KidsLearning/KidsGameScreen'));
const KidsProgressScreen = lazyRetry(() => import('./components/Social/KidsLearning/KidsProgressScreen'));
const GameCreatorScreen = lazyRetry(() => import('./components/Social/KidsLearning/GameCreatorScreen'));
const CustomGamesScreen = lazyRetry(() => import('./components/Social/KidsLearning/CustomGamesScreen'));

// Agent Chat
const AgentChatPage = lazyRetry(() => import('./components/Social/Chat/AgentChatPage'));

// Thought Experiment Tracker
const ThoughtExperimentTracker = lazyRetry(() => import('./components/Social/Tracker/ThoughtExperimentTracker'));

// Agent Hive View
const AgentHiveView = lazyRetry(() => import('./components/Social/Tracker/AgentHiveView'));

// Settings
const BackupSettingsPage = lazyRetry(() => import('./components/Social/Settings/BackupSettingsPage'));
const ThemeSettingsPage = lazyRetry(() => import('./components/Social/Settings/ThemeSettingsPage'));

// Agent Audit
const AgentAuditPage = lazyRetry(() => import('./components/Social/Agents/AgentAuditPage'));

// Agent Profile
const AgentProfilePage = lazyRetry(() => import('./components/Social/Agents/AgentProfilePage'));

// Autopilot
const AutopilotPage = lazyRetry(() => import('./components/Social/Autopilot/AutopilotPage'));

// MCP Tool Browser
const MCPToolBrowser = lazyRetry(() => import('./components/Social/Tools/MCPToolBrowser'));

// Marketplace
const MarketplacePage = lazyRetry(() => import('./components/Social/Marketplace/MarketplacePage'));

// Channels
const ChannelBindingsPage = lazyRetry(() => import('./components/Channels/ChannelBindingsPage'));
const ConversationHistoryPanel = lazyRetry(() => import('./components/Channels/ConversationHistoryPanel'));

// Share Landing
const ShareLandingPage = lazy(() => import('./pages/ShareLandingPage'));
const PupitDocs = lazy(() => import('./components/Pupit/PupitDocs'));
const PupitAi = lazy(() => import('./components/Pupit/PupitAi'));

// Mindstory (social-integrated video generation)
const MindstoryPage = lazyRetry(() => import('./components/Social/Mindstory/MindstoryPage'));

// Hive Contest (HARTOS-backed live ideas wall + leaderboard + Claude Code onramp)
const HiveContestPage = lazyRetry(() => import('./components/Social/HiveContest/HiveContestPage'));

// Blog (SEO surface - markdown content rendered with Helmet meta).
// Index lists all posts; individual posts use existing JS pages.

// Download - dedicated product detail page with system requirements,
// install steps, and the same canonical NUNBA_DOWNLOAD_URL used by the
// Agent.js hero CTA.  Stage 3 of the systematic 10K-user funnel:
// landing CTA → /download deep-link → install.

// Press - media kit (logo, one-liners, key numbers, founder quotes,
// boilerplate) for journalists / bloggers covering Nunba.  Stage 10
// of the systematic funnel: makes any organic coverage frictionless.

// News - public SEO surface for Nunba releases + community updates.
// Static registry (newsData.js) is the crawlable content; the index page
// layers live Nunba community/trending data on top and every article deep-
// links back into the community pages. Sitemap mirrors the registry.

// Listings - generic multi-vertical surface (marketplace/rentals/housing/…)
// driven by the vertical registry in components/Listings/verticals.js.
// Public + SEO'd; live data or honest empty states, never fabricated rows.
// See docs/DISTRIBUTED_PLATFORM_PLAN.md for the platform-wide plan.

// Research - ~100 SEO pages of real AI/BCI papers (Nature + arXiv metadata,
// registry at src/data/researchPapers.json via `npm run research:pull`).
// Explanations enriched over time by the HARTOS paper-explanation goal.

// Join - referral landing for /join?ref=<code> (the URL emitted by
// HARTOS DistributionService.invite_share_url).  Stage 5 of the funnel:
// existing user invites friend → friend lands on /join → captures ref
// code → downloads.  Until this route existed, every viral attempt
// landed on a 404.
const Join = lazyRetry(() => import('./pages/Join'));

function NotFoundPage() {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0F0E17', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', textAlign: 'center', px: 3 }}>
      <PageMeta
        title="404: Page Not Found | Hevolve AI"
        noindex
      />
      <Typography variant="h1" sx={{ fontSize: { xs: '4rem', sm: '6rem' }, fontWeight: 700, background: 'linear-gradient(135deg, #6C63FF, #FF6B6B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 2 }}>404</Typography>
      <Typography variant="h5" sx={{ mb: 1, opacity: 0.87 }}>Page not found</Typography>
      <Typography variant="body1" sx={{ mb: 4, opacity: 0.6, maxWidth: 400 }}>The page you're looking for doesn't exist or has been moved.</Typography>
      <Button component={Link} to="/social" variant="contained" sx={{ bgcolor: '#6C63FF', '&:hover': { bgcolor: '#5A52E0' }, borderRadius: 3, px: 4, py: 1.5, textTransform: 'none', fontWeight: 600 }}>Go Home</Button>
    </Box>
  );
}

// Admin pages
const AdminLayout = lazyRetry(() => import('./components/Admin/AdminLayout'));
const AdminDashboard = lazyRetry(() => import('./components/Admin/DashboardPage'));
const AdminUsers = lazyRetry(() => import('./components/Admin/UsersManagementPage'));
const AdminModeration = lazyRetry(() => import('./components/Admin/ModerationPage'));
const AdminAgentSync = lazyRetry(() => import('./components/Admin/AgentSyncPage'));
const AdminChannels = lazyRetry(() => import('./components/Admin/ChannelsPage'));
const AdminWorkflows = lazyRetry(() => import('./components/Admin/WorkflowsPage'));
const AdminSettings = lazyRetry(() => import('./components/Admin/SettingsPage'));
const AdminIdentity = lazyRetry(() => import('./components/Admin/IdentityPage'));
const AdminAgentDashboard = lazyRetry(() => import('./components/Admin/AgentDashboardPage'));
const AdminRevenue = lazyRetry(() => import('./components/Admin/RevenueAnalyticsPage'));
const AdminPages = lazyRetry(() => import('./components/Admin/PagesPage'));
const AdminContentTasks = lazyRetry(() => import('./components/Admin/ContentTasksPage'));
const AdminProviderManagement = lazyRetry(() => import('./components/Admin/ProviderManagementPage'));
const Partners = lazyRetry(() => import('./pages/SubPages/Multipurpose/partners'));

function MainRoutes() {
  // Self-referential, not a fixed homepage URL. A canonical is not a fallback:
  // it names the URL a crawler should index INSTEAD of this one, so the literal
  // 'https://hevolve.ai/' that used to sit below told every page that did not
  // override it that it was a duplicate of the homepage. Eleven prerendered
  // pages inherited exactly that, measured from build/ on 2026-08-04: all nine
  // /social routes plus /docs and /pupit, each one also carrying
  // `robots: index, follow` and a sitemap entry. An alias that really is a
  // duplicate, /Plan for instance, still sets its own and still wins here.

  return (
    <>
      {/* Global fallback SEO.
          These used to be hardcoded in public/index.html, which made them
          impossible to override: that template renders on every route, so the
          static tags landed ahead of each page's own and search engines and
          share cards used the homepage values for all 100+ research papers.
          Declared here instead, react-helmet-async replaces them wherever a
          page sets its own and falls back to these where it does not. */}
      {/* The site-wide fallback, and it goes through PageMeta like every page
          does. react-helmet-async lets the deeper declaration win, so a page
          that sets its own replaces these; a page that sets nothing gets a
          complete set rather than a title and a homepage canonical.

          The invariant tags (og:image and its dimensions, og:locale,
          og:site_name, twitter:card/image/site) stay in public/index.html.
          Duplicating them here would recreate the conflict that put homepage
          share cards on 100+ research papers. */}
      <PageMeta
        title="Hevolve AI | Self-Evolving Multimodal AI Agents"
        description="Create self-evolving multimodal AI agents through natural conversation and real-time learning."
      />

      <Routes>
        <Route
          index
          path="/"
          element={
            <>
              <PageMeta
                title="Hevolve AI | Self-Evolving Multimodal AI Agents"
                description="Build self-evolving multimodal AI agents using natural conversation and no-code tools."
                path="/"
              />
              <Agent key="root" />
            </>
          }
        />

        {/* Download - product page (Stage 3 of 10K-user funnel) */}
        <Route
          path="/download"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Download />
            </Suspense>
          }
        />
        {/* The plural is what people type and say; it served the homepage. */}
        <Route path="/downloads" element={<Navigate to="/download" replace />} />

        {/* Press - media kit (Stage 10 of funnel) */}
        <Route
          path="/press"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Press />
            </Suspense>
          }
        />

        {/* Partners — the component was written (SubPages/Multipurpose/
            partners.js, with the Microsoft for Startups and NVIDIA Inception
            badges) and never given a route, so the footer's "Our Partners" link
            served the 404 page while the page itself sat in the bundle. */}
        <Route
          path="/partners"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Partners />
            </Suspense>
          }
        />

        {/* News - SEO-indexable Nunba news + community updates */}
        <Route
          path="/news"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <NewsIndex />
            </Suspense>
          }
        />
        <Route
          path="/news/:slug"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <NewsArticlePage />
            </Suspense>
          }
        />

        {/* Research - AI/BCI papers explained (SEO surface) */}
        <Route
          path="/research"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <ResearchIndex />
            </Suspense>
          }
        />
        <Route
          path="/research/:slug"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <ResearchPaperPage />
            </Suspense>
          }
        />

        {/* Answers - question-shaped entry points. A paper title is nobody's
            search query; a question is. These rank, and cite the research
            pages as evidence, which is also how those pages get reached. */}
        <Route
          path="/answers"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <AnswerIndex />
            </Suspense>
          }
        />
        <Route
          path="/answers/:slug"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <AnswerPage />
            </Suspense>
          }
        />

        {/* Earn: how the hive pays the people who power it. Eager import (not
            lazy) like the other prerendered SEO pages, so the first render
            matches the captured HTML and there is no #418 hydration mismatch. */}
        <Route
          path="/earn"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <EarnPage />
            </Suspense>
          }
        />

        {/* The third audience page, beside /developers (build with it) and
            /earn (get paid by it): the person who just wants a job done. Same
            eager import and Suspense shape as /earn so the prerendered first
            paint matches the captured HTML. */}
        <Route
          path="/jobs"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <JobsPage />
            </Suspense>
          }
        />

        {/* HARTOS core/install_links.py maps ('ios','default') to
            https://hevolve.ai/ios-coming-soon, so the agent has been handing
            that URL to every iPhone user who asked for the app. It was never a
            route, so all of them landed on the homepage with no explanation. */}
        <Route
          path="/ios-coming-soon"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <IosComingSoonPage />
            </Suspense>
          }
        />

        {/* The two URLs Meta App Review and Google Play both require, and that
            the footer has been linking all along. Before this /privacy 302'd
            to a login wall and /data-deletion served the homepage. Several
            aliases, because the footer, the app stores and people all reach
            for different ones. */}
        {/* Written out one per line rather than mapped over an array. A
            .map() with path={p} is invisible to scripts/verify-mirrors.js,
            which reads literal path="..." strings out of this file, so
            registering routes dynamically would hide them from the guard that
            exists to prove every link lands somewhere. Verified: the mapped
            version made the checker report /privacy-policy as unrouted. */}
        {/* CANONICAL. Kong owns the /privacy prefix.
            Route `hertzai-privacy` (path /privacy) prefix-matches, so it
            swallowed /privacy-policy and /privacy-notice too, stripped the
            matched prefix and 302'd the remainder to a legacy login:
            /privacy-policy became /login?redirectTo=%2F-policy. The page was
            being served correctly by the origin on :3005 the whole time and
            never reached a visitor. /legal/* is not claimed by any Kong route,
            verified against the live gateway. */}
        <Route path="/legal/privacy" element={<Suspense fallback={<PageSkeleton />}><PrivacyPolicyPage /></Suspense>} />
        <Route path="/privacy-policy" element={<Suspense fallback={<PageSkeleton />}><PrivacyPolicyPage /></Suspense>} />
        <Route path="/privacy" element={<Suspense fallback={<PageSkeleton />}><PrivacyPolicyPage /></Suspense>} />
        {/* /terms and /termsandconditions are deliberately NOT aliased here.
            Terms of service and a privacy policy are different documents that
            make different promises, and serving one under the other's URL is
            worse than a 404: a reader who wants to know what they agreed to
            gets a page about data handling and no way to tell it is the wrong
            document. They need writing; until then the footer does not link
            them. */}
        <Route path="/data-deletion" element={<Suspense fallback={<PageSkeleton />}><DataDeletionPage /></Suspense>} />
        <Route path="/delete-account" element={<Suspense fallback={<PageSkeleton />}><DataDeletionPage /></Suspense>} />
        <Route path="/deleteUser" element={<Suspense fallback={<PageSkeleton />}><DataDeletionPage /></Suspense>} />

        {/* Incident write-ups: real failures in this codebase. Same registry
            pattern as answers -- one JSON entry is the route, the prerender
            target and the sitemap URL. */}
        <Route
          path="/incidents"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <IncidentIndex />
            </Suspense>
          }
        />
        <Route
          path="/incidents/:slug"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <IncidentPage />
            </Suspense>
          }
        />

        {/* Listings - generic multi-vertical surface */}
        <Route
          path="/listings"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <ListingsPage />
            </Suspense>
          }
        />
        <Route
          path="/listings/:vertical"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <ListingsPage />
            </Suspense>
          }
        />

        {/* Join - referral landing (Stage 5: viral loop entry point) */}
        <Route
          path="/join"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Join />
            </Suspense>
          }
        />

        {/* Blog - SEO surface (#B4 fix 2026-05-23: was unrouted) */}
        <Route
          path="/blog"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <BlogIndex />
            </Suspense>
          }
        />
        <Route
          path="/blog/:slug"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <BlogDynamicPage />
            </Suspense>
          }
        />
        <Route
          path="/hive"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <HiveCensus />
            </Suspense>
          }
        />
        {/* How the learning engine works: a Remotion film and an explainer.
            Underscore canonical like /hive_contest, hyphenated alias below. */}
        <Route
          path="/how_it_works"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <HowItWorks />
            </Suspense>
          }
        />
        <Route path="/how-it-works" element={<Navigate to="/how_it_works" replace />} />
        <Route
          path="/blog/the-node-that-keeps-what-it-learns"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  title="The Node That Keeps What It Learns | Hevolve"
                  description="The learning engine inside HARTOS writes what it learns into its weights, on your machine, and still knows it tomorrow. Measured against fine-tuning and replay."
                  ogTitle="The Node That Keeps What It Learns"
                  ogDescription="The learning engine inside HARTOS writes what it learns into its weights, on your machine, and still knows it tomorrow."
                  path="/blog/the-node-that-keeps-what-it-learns"
                  type="article"
                >
                  <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@type': 'BlogPosting', headline: 'The Node That Keeps What It Learns', description: 'The learning engine inside HARTOS writes what it learns into its weights, on your machine, and still knows it tomorrow. Measured against fine-tuning and replay.', url: 'https://hevolve.ai/blog/the-node-that-keeps-what-it-learns', datePublished: '2026-07-27', author: { '@type': 'Organization', name: 'Hevolve AI' }, publisher: { '@type': 'Organization', name: 'Hevolve AI', url: 'https://hevolve.ai' }, mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://hevolve.ai/blog/the-node-that-keeps-what-it-learns' }, })}</script>
                </PageMeta>
                <BlogLearningEnginePost />
              </>
            </Suspense>
          }
        />
        <Route
          path="/blog/reasoning-with-time-and-ai"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  title="Reasoning With Time: Self-Evolving AI Beats Static Models | Hevolve"
                  description="How Hevolve agents reason across time, refining their answers from cumulative experience rather than the snapshot of a frozen model."
                  ogTitle="Reasoning With Time: Self-Evolving AI Beats Static Models"
                  path="/blog/reasoning-with-time-and-ai"
                  type="article"
                >
                  <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@type': 'BlogPosting', headline: 'Reasoning With Time: Self-Evolving AI Beats Static Models', description: 'How Hevolve agents reason across time, refining their answers from cumulative experience rather than the snapshot of a frozen model.', url: 'https://hevolve.ai/blog/reasoning-with-time-and-ai', datePublished: '2026-04-12', author: { '@type': 'Organization', name: 'Hevolve AI' }, publisher: { '@type': 'Organization', name: 'Hevolve AI', url: 'https://hevolve.ai' }, mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://hevolve.ai/blog/reasoning-with-time-and-ai' }, })}</script>
                </PageMeta>
                <BlogReasoningPost />
              </>
            </Suspense>
          }
        />
        <Route
          path="/blog/run-local-ai-on-8gb-ram"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  title="How Nunba Runs a 4B LLM on 8GB RAM: Speculative Decoding Explained | Hevolve"
                  description="Speculative decoding + tiny draft model + quantized main model = local AI that's actually responsive on the laptop you already own. Here is how Nunba's pipeline fits."
                  ogTitle="How Nunba Runs a 4B LLM on 8GB RAM: Speculative Decoding Explained"
                  ogDescription="Speculative decoding + tiny draft model + quantized main model = local AI that's responsive on the laptop you already own."
                  path="/blog/run-local-ai-on-8gb-ram"
                  type="article"
                >
                  <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@type': 'BlogPosting', headline: 'How Nunba Runs a 4B LLM on 8GB RAM: Speculative Decoding Explained', description: 'Speculative decoding + tiny draft model + quantized main model = local AI that\'s responsive on the laptop you already own.', url: 'https://hevolve.ai/blog/run-local-ai-on-8gb-ram', datePublished: '2026-05-23', author: { '@type': 'Organization', name: 'Hevolve AI' }, publisher: { '@type': 'Organization', name: 'Hevolve AI', url: 'https://hevolve.ai' }, mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://hevolve.ai/blog/run-local-ai-on-8gb-ram' }, })}</script>
                </PageMeta>
                <BlogRunLocalAiPost />
              </>
            </Suspense>
          }
        />
        <Route
          path="/blog/privacy-first-ai"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  title="Privacy-First AI: Local-First Multimodal Without the Cloud Tax | Hevolve"
                  description="Why Hevolve runs the entire chat / vision / voice loop on your machine, what we send to the cloud (almost nothing), and how the federated layer respects your data."
                  ogTitle="Privacy-First AI: Local-First Multimodal Without the Cloud Tax"
                  ogDescription="Why Hevolve runs the entire chat / vision / voice loop on your machine, privacy-first AI without the cloud tax."
                  path="/blog/privacy-first-ai"
                  type="article"
                >
                  <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@type': 'BlogPosting', headline: 'Privacy-First AI: Local-First Multimodal Without the Cloud Tax', description: 'Why Hevolve runs the entire chat / vision / voice loop on your machine, what we send to the cloud (almost nothing), and how the federated layer respects your data.', url: 'https://hevolve.ai/blog/privacy-first-ai', datePublished: '2026-04-18', author: { '@type': 'Organization', name: 'Hevolve AI' }, publisher: { '@type': 'Organization', name: 'Hevolve AI', url: 'https://hevolve.ai' }, mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://hevolve.ai/blog/privacy-first-ai' }, })}</script>
                </PageMeta>
                <BlogPrivacyPost />
              </>
            </Suspense>
          }
        />

        {/* SEO: Canonical lowercase route */}
        <Route
          path="/about"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  title="About Hevolve AI | Continual Learning Platform"
                  description="Learn about Hevolve AI and how we enable self-evolving AI agents through continual learning and natural conversation."
                  path="/about"
                />
                <Home />
              </>
            </Suspense>
          }
        />
        {/* SEO: 301-equivalent redirect from old PascalCase URL */}
        <Route path="/AboutHevolve" element={<Navigate to="/about" replace />} />

        <Route
          path="/personalisedlearning"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  title="Personalised Learning AI | Hevolve AI"
                  description="Create personalised AI tutors that adapt and learn continuously."
                  path="/personalisedlearning"
                />
                <Personalised_Learning />
              </>
            </Suspense>
          }
        />

        <Route
          path="/aboutus"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  title="About Us | Hevolve AI"
                  description="Meet the team behind Hevolve AI and our vision for self-evolving AI systems."
                  path="/aboutus"
                />
                <AboutUs />
              </>
            </Suspense>
          }
        />

        {/* SEO: Canonical lowercase pricing route */}
        <Route
          path="/pricing"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  title="Pricing Plans | Hevolve AI: Self-Evolving AI Agents"
                  description="Explore Hevolve AI pricing plans for individuals, institutions, and enterprises. Start free with our trial plan."
                  path="/pricing"
                />
                <Pricing />
              </>
            </Suspense>
          }
        />
        {/* SEO: Redirect old PascalCase URL */}
        <Route path="/Plan" element={<Navigate to="/pricing" replace />} />

        <Route
          path="/speechtherapy"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  title="Speech Therapy AI | Hevolve AI"
                  description="Practice speech therapy using AI-powered conversational agents."
                  path="/speechtherapy"
                />
                <SpeechTherapyPage />
              </>
            </Suspense>
          }
        />

        <Route
          path="/trialplan"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  title="Trial Plan | Hevolve AI"
                  description="Start a trial of Hevolve AI and explore self-evolving AI agents."
                  path="/trialplan"
                />
                <TrialPlanPricing />
              </>
            </Suspense>
          }
        />

        {/* Payment routes */}
        <Route
          path="/PaymentFailure"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  robots="noindex, nofollow"
                />
                <PaymentFailure />
              </>
            </Suspense>
          }
        />

        <Route
          path="/PaymentSuccess"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  robots="noindex, nofollow"
                />
                <PaymentSuccess />
              </>
            </Suspense>
          }
        />

        <Route
          path="/PendingPaymentPage"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  robots="noindex, nofollow"
                />
                <PendingPaymentPage />
              </>
            </Suspense>
          }
        />

        <Route
          path="/contact"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  title="Contact Hevolve AI"
                  description="Contact Hevolve AI for support, partnerships, or enterprise solutions."
                  path="/contact"
                />
                <Contact />
              </>
            </Suspense>
          }
        />

        <Route
          path="/institution"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  title="Institutional AI Solutions | Hevolve AI"
                  description="AI solutions for institutions and organizations."
                  path="/institution"
                />
                <Institution />
              </>
            </Suspense>
          }
        />

        <Route
          path="/institution/signup"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  title="Institution Signup | Hevolve AI"
                  description="Sign up your institution to deploy Hevolve AI agents."
                  path="/institution/signup"
                />
                <SignupLite />
              </>
            </Suspense>
          }
        />

        <Route
          path="/agents"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  title="AI Agents | Hevolve AI"
                  description="Browse AI agents available on Hevolve AI."
                  path="/agents"
                />
                <Agents />
              </>
            </Suspense>
          }
        />

        <Route
          path="/agents/:agentName/about"
          element={
            <Suspense fallback={<PageSkeleton dark />}>
              <AgentLanding />
            </Suspense>
          }
        />

        <Route
          path="/agents/:agentName"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  title="AI Agent | Hevolve AI"
                  description="Explore this self-evolving AI agent on Hevolve AI, learn, interact, and evolve through natural conversation."
                />
                <Agent />
              </>
            </Suspense>
          }
        />

        <Route
          path="/signup"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <>
                <PageMeta
                  title="Signup | Hevolve AI"
                  description="Create your Hevolve AI account and start building AI agents."
                  path="/signup"
                />
                <NewSignup />
              </>
            </Suspense>
          }
        />
        {/* Sign in — the OTP/Google auth (OtpAuthModal) on its own route, so the
            header's Sign in and the register page have a real internal
            destination instead of the external hevolvechat business login. */}
        <Route
          path="/signin"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <SigninPage />
            </Suspense>
          }
        />
        {/* Share link resolver - resolves /s/:token and redirects to actual resource */}
        <Route path="/s/:token" element={<Suspense fallback={<PageSkeleton dark />}><ShareLandingPage /></Suspense>} />
        {/* Mindstory SDK + Pupit documentation */}
        <Route path="/docs" element={<Suspense fallback={<PageSkeleton />}><><PageMeta
  title="Mindstory SDK Documentation | Hevolve AI"
  description="Mindstory multimodal SDK: completions API, Pupit video player, website plugin documentation."
/><PupitDocs /></></Suspense>} />
        {/* /docs/<guide> — each guide is addressable.
            The five guides were tab state with no URL, so none of them could be
            linked to, and the header's Developer Guide menu pointed at
            /pupitdocs and /hevolvedocs, which had no routes and served the 404
            page. The content was there the whole time, behind buttons on a URL
            that could not address it. */}
        <Route path="/docs/:tab" element={<Suspense fallback={<PageSkeleton />}><><PageMeta
  title="Developer Documentation | Hevolve AI"
  description="Mindstory SDK, Pupit video API, website widget and Hive API documentation for HART OS and Hevolve AI."
/><PupitDocs /></></Suspense>} />
        {/* The URLs the docs and the header have been advertising for months.
            Redirects rather than duplicate routes, so there is one canonical
            address per guide and these keep working for anyone who bookmarked
            them or read them in an email. */}
        <Route path="/hevolvedocs" element={<Navigate to="/docs/widget" replace />} />
        <Route path="/pupitdocs" element={<Navigate to="/docs/client" replace />} />
        <Route path="/hivedocs" element={<Navigate to="/docs/hive" replace />} />
        <Route path="/pupit" element={<Suspense fallback={<PageSkeleton dark />}><><PageMeta
  title="Pupit AI: Video Generation | Hevolve AI"
  description="Generate AI-powered talking head videos with Pupit."
/><PupitAi /></></Suspense>} />
        {/* Hive Contest - canonical top-level URL hevolve.ai/hive_contest.
            All HARTOS workflow / Nunba agent links / docs land here. */}
        <Route path="/hive_contest" element={<Suspense fallback={<PageSkeleton dark />}><><PageMeta
  title="Hive Contest | Hevolve AI"
  description="Submit hive-contest ideas, watch the live floating ideas wall, and plug Claude Code into HARTOS."
  path="/hive_contest"
/><HiveContestPage /></></Suspense>} />
        {/* Hyphenated alias - common typo / older link form */}
        <Route path="/hive-contest" element={<Navigate to="/hive_contest" replace />} />

        <Route path="/social" element={<Suspense fallback={<PageSkeleton dark variant="feed" />}><SocialHome /></Suspense>}>
          {/* Open routes - guests and anonymous can read */}
          {/* The feed, and titled as the feed.
              It had carried "Hevolve AI: Thought Experiments" while
              /social/experiments -- the page that actually IS thought
              experiments -- carried no title and fell back to the site
              default. Two pages describing each other's contents, found
              2026-08-17 once both were prerendered and their <title> could be
              read from the served HTML. This sidebar calls /social "Feed". */}
          <Route index element={<><PageMeta
  title="Hevolve AI: Feed"
  description="The Hevolve AI feed: what agents and people are building, proposing, and learning right now."
/><SocialFeed /></>} />
          <Route path="profile/:userId" element={<><PageMeta
  title="Hevolve AI: Profile"
/><SocialProfile /></>} />
          <Route path="post/:postId" element={<><PageMeta
  title="Hevolve AI: Post"
/><PostDetailPage /></>} />
          <Route path="search" element={<><PageMeta
  title="Hevolve AI: Search"
  description="Search thought experiments, users, and agents on Hevolve AI."
/><SearchPage /></>} />
          <Route path="achievements" element={<><PageMeta
  title="Hevolve AI: Achievements"
/><SocialAchievements /></>} />
          <Route path="challenges" element={<><PageMeta
  title="Hevolve AI: Challenges"
/><ChallengesPage /></>} />
          <Route path="challenges/:challengeId" element={<><PageMeta
  title="Hevolve AI: Challenge"
  description="Take on AI-powered challenges and earn achievements on Hevolve AI."
/><ChallengeDetailPage /></>} />
          {/* Legacy nested path → canonical top-level /hive_contest */}
          <Route path="hive-contest" element={<Navigate to="/hive_contest" replace />} />
          <Route path="hive_contest" element={<Navigate to="/hive_contest" replace />} />
          <Route path="seasons" element={<><PageMeta
  title="Hevolve AI: Seasons"
/><SeasonPage /></>} />
          <Route path="recipes" element={<><PageMeta
  title="Hevolve AI: Recipes"
/><RecipeListPage /></>} />
          <Route path="recipes/:recipeId" element={<><PageMeta
  title="Hevolve AI: Agent Recipe"
  description="Explore AI agent recipes and workflows on Hevolve AI."
/><RecipeDetailPage /></>} />
          <Route path="communities" element={<><PageMeta
  title="Hevolve AI: Communities"
/><CommunityListPage /></>} />
          <Route path="h/:communityId" element={<><PageMeta
  title="Hevolve AI: Community"
  description="Join AI communities, share thought experiments, and collaborate on Hevolve AI."
/><CommunityDetailPage /></>} />
          <Route path="agents/:agentId/evolution" element={<><PageMeta
  title="Hevolve AI: Agent Evolution"
  description="Track how your AI agent evolves and learns over time on Hevolve AI."
/><AgentEvolutionPage /></>} />
          <Route path="agent/:agentId" element={<Suspense fallback={<PageSkeleton dark />}><PageMeta
  title="Hevolve AI: Agent Profile"
/><AgentProfilePage /></Suspense>} />
          <Route path="agent/:agentId/chat" element={<><PageMeta
  title="Hevolve AI: Agent Chat"
/><AgentChatPage /></>} />
          <Route path="coding" element={<><PageMeta
  title="Hevolve AI: Coding Agent"
/><AgentChatPage /></>} />
          <Route path="tracker" element={<RoleGuard minRole="flat"><PageMeta
  title="Hevolve AI: Experiment Tracker"
/><ThoughtExperimentTracker /></RoleGuard>} />
          <Route path="hive" element={<RoleGuard minRole="flat"><PageMeta
  title="Hevolve AI: Agent Hive"
/><AgentHiveView /></RoleGuard>} />
          <Route path="settings/backup" element={<RoleGuard minRole="guest"><PageMeta
  title="Hevolve AI: Backup &amp; Sync"
/><BackupSettingsPage /></RoleGuard>} />
          <Route path="settings/appearance" element={<RoleGuard minRole="guest"><PageMeta
  title="Hevolve AI: Appearance"
/><ThemeSettingsPage /></RoleGuard>} />
          <Route path="agents" element={<RoleGuard minRole="flat"><PageMeta
  title="Hevolve AI: Agent Audit"
/><AgentAuditPage /></RoleGuard>} />
          <Route path="autopilot" element={<Suspense fallback={<PageSkeleton dark />}><PageMeta
  title="Hevolve AI: Autopilot"
/><AutopilotPage /></Suspense>} />
          <Route path="tools" element={<><PageMeta
  title="Hevolve AI: MCP Tools"
/><MCPToolBrowser /></>} />
          <Route path="marketplace" element={<><PageMeta
  title="Hevolve AI: Marketplace"
/><MarketplacePage /></>} />
          <Route path="channels" element={<RoleGuard minRole="flat"><PageMeta
  title="Hevolve AI: Channels"
/><ChannelBindingsPage /></RoleGuard>} />
          <Route path="channels/history" element={<RoleGuard minRole="flat"><PageMeta
  title="Hevolve AI: Conversation History"
/><ConversationHistoryPanel /></RoleGuard>} />

          {/* Kids Learning Zone - open access (educational content, no auth wall) */}
          <Route path="kids" element={<><PageMeta
  title="Kids Learning Zone: AI-Powered Educational Games | Hevolve AI"
  description="Fun, AI-powered educational games for kids. Personalized learning through interactive play on Hevolve AI."
/><KidsLearningHub /></>} />
          <Route path="kids/game/:gameId" element={<><PageMeta
  title="Learning Game | Hevolve AI Kids"
  description="Play and learn with AI-powered educational games designed for children on Hevolve AI."
/><KidsGameScreen /></>} />
          <Route path="kids/progress" element={<RoleGuard minRole="guest"><KidsProgressScreen /></RoleGuard>} />
          <Route path="kids/create" element={<RoleGuard minRole="guest"><GameCreatorScreen /></RoleGuard>} />
          <Route path="kids/custom" element={<RoleGuard minRole="flat"><CustomGamesScreen /></RoleGuard>} />

          {/* Games Hub - open for browsing, flat+ for multiplayer */}
          <Route path="games" element={<><PageMeta
  title="Games Hub: AI-Powered Games | Hevolve AI"
  description="Discover and play AI-powered games on Hevolve AI's Games Hub."
/><GameHub /></>} />
          <Route path="games/:gameId" element={<><PageMeta
  title="Hevolve AI: Play Game"
  description="Play AI-powered games on Hevolve AI."
/><UnifiedGameScreen /></>} />

          {/* Mindstory - AI video generation (reuses existing PupitCard + VIDEO_GEN_URL) */}
          <Route path="mindstory" element={<><PageMeta
  title="Mindstory: AI Video Generation | Hevolve AI"
  description="Create AI-powered talking head videos with Mindstory on Hevolve AI."
/><MindstoryPage /></>} />

          {/* Auth required - guests allowed for read-only */}
          <Route path="resonance" element={<RoleGuard minRole="flat" allowGuest><SocialResonance /></RoleGuard>} />

          {/* Auth required - flat+ only */}
          <Route path="notifications" element={<RoleGuard minRole="flat"><NotificationsPage /></RoleGuard>} />
          <Route path="regions" element={<RoleGuard minRole="flat"><RegionsPage /></RoleGuard>} />
          <Route path="regions/:regionId" element={<RoleGuard minRole="flat"><RegionDetailPage /></RoleGuard>} />
          <Route path="hub" element={<><PageMeta
  title="Hevolve AI: Activity Hub"
  description="See what agents and people are doing right now across Hevolve AI — live activity, streaks, and what changed since you were last here."
/><ActivityHub /></>} />
          <Route path="experiments" element={<><PageMeta
  title="Hevolve AI: Thought Experiments"
  description="Browse thought experiments proposed by the community, back the ones you believe in, and watch them run."
/><ExperimentDiscoveryPage /></>} />
          <Route path="compute" element={<RoleGuard minRole="flat"><ComputeDashboardPage /></RoleGuard>} />
          <Route path="encounters" element={<RoleGuard minRole="flat"><EncountersPage /></RoleGuard>} />
          <Route path="encounters/:encounterId" element={<RoleGuard minRole="flat"><EncounterDetailPage /></RoleGuard>} />
          <Route path="campaigns" element={<RoleGuard minRole="flat"><CampaignsPage /></RoleGuard>} />
          <Route path="campaigns/:campaignId" element={<RoleGuard minRole="flat"><CampaignDetailPage /></RoleGuard>} />

          {/* Regional+ only - campaign creation */}
          <Route path="campaigns/create" element={<RoleGuard minRole="regional"><CampaignStudio /></RoleGuard>} />
        </Route>

        {/* Admin Routes - ErrorBoundary lives inside AdminLayout (AOP pattern),
             so content crashes don't kill the sidebar/nav shell. */}
        <Route path="/admin" element={<Suspense fallback={<PageSkeleton dark />}><AdminLayout><RoleGuard minRole="guest" fallback="/social"><AdminDashboard /></RoleGuard></AdminLayout></Suspense>} />
        <Route path="/admin/users" element={<Suspense fallback={<PageSkeleton dark />}><AdminLayout><RoleGuard minRole="central" fallback="/admin"><AdminUsers /></RoleGuard></AdminLayout></Suspense>} />
        <Route path="/admin/moderation" element={<Suspense fallback={<PageSkeleton dark />}><AdminLayout><RoleGuard minRole="central" fallback="/admin"><AdminModeration /></RoleGuard></AdminLayout></Suspense>} />
        <Route path="/admin/agents" element={<Suspense fallback={<PageSkeleton dark />}><AdminLayout><RoleGuard minRole="guest" fallback="/social"><AdminAgentSync /></RoleGuard></AdminLayout></Suspense>} />
        <Route path="/admin/channels" element={<Suspense fallback={<PageSkeleton dark />}><AdminLayout><RoleGuard minRole="guest" fallback="/social"><AdminChannels /></RoleGuard></AdminLayout></Suspense>} />
        <Route path="/admin/workflows" element={<Suspense fallback={<PageSkeleton dark />}><AdminLayout><RoleGuard minRole="guest" fallback="/social"><AdminWorkflows /></RoleGuard></AdminLayout></Suspense>} />
        <Route path="/admin/settings" element={<Suspense fallback={<PageSkeleton dark />}><AdminLayout><RoleGuard minRole="guest" fallback="/social"><AdminSettings /></RoleGuard></AdminLayout></Suspense>} />
        <Route path="/admin/identity" element={<Suspense fallback={<PageSkeleton dark />}><AdminLayout><RoleGuard minRole="guest" fallback="/social"><AdminIdentity /></RoleGuard></AdminLayout></Suspense>} />
        <Route path="/admin/agent-dashboard" element={<Suspense fallback={<PageSkeleton dark />}><AdminLayout><RoleGuard minRole="guest" fallback="/social"><AdminAgentDashboard /></RoleGuard></AdminLayout></Suspense>} />
        <Route path="/admin/revenue" element={<Suspense fallback={<PageSkeleton dark />}><AdminLayout><RoleGuard minRole="central" fallback="/admin"><AdminRevenue /></RoleGuard></AdminLayout></Suspense>} />
        <Route path="/admin/pages" element={<Suspense fallback={<PageSkeleton dark />}><AdminLayout><RoleGuard minRole="central" fallback="/admin"><AdminPages /></RoleGuard></AdminLayout></Suspense>} />
        <Route path="/admin/content-tasks" element={<Suspense fallback={<PageSkeleton dark />}><AdminLayout><RoleGuard minRole="guest" fallback="/social"><AdminContentTasks /></RoleGuard></AdminLayout></Suspense>} />
        <Route path="/admin/providers" element={<Suspense fallback={<PageSkeleton dark />}><AdminLayout><RoleGuard minRole="guest" fallback="/social"><AdminProviderManagement /></RoleGuard></AdminLayout></Suspense>} />

        {/* 404 catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

export default MainRoutes;
